import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Loader2, Home, ArrowRight, ArrowLeft, Check, LayoutDashboard, ShieldCheck, Users, Bot, Bell, ChevronLeft } from "lucide-react";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { SuburbSelect } from "@/components/SuburbSelect";


const slides = [
    {
        id: "home",
        title: "Recommended Actions",
        description: "Your home's health at a glance. Track your home score, see upcoming services, and manage daily tasks like plant watering.",
        icon: LayoutDashboard,
        color: "text-blue-500",
        bg: "bg-blue-500/10"
    },
    {
        id: "home-records",
        title: "Your Home Records",
        description: "Your digital home record book. Add appliances to receive smart reminders, ensuring you never miss a critical service interval again.",
        icon: ShieldCheck,
        color: "text-emerald-500",
        bg: "bg-emerald-500/10"
    },
    {
        id: "directory",
        title: "Community Providers",
        description: "Add providers you've used to help build a verified directory. Join a community group to see exactly which professionals your neighbors are using.",
        icon: Users,
        color: "text-purple-500",
        bg: "bg-purple-500/10"
    },
    {
        id: "ai",
        title: "Ask HomeEzi",
        description: "Your personal home assistant. Ask specific questions about your appliances, paint codes, or get general maintenance advice.",
        icon: Bot,
        color: "text-indigo-500",
        bg: "bg-indigo-500/10"
    },
    {
        id: "notifications",
        title: "Stay Notified",
        description: "Never miss a beat. Customize your notification preferences to get alerts for warranty expiries and maintenance tasks.",
        icon: Bell,
        color: "text-amber-500",
        bg: "bg-amber-500/10"
    }
];

const OnboardingModal = ({ open, onOpenChange, onComplete }) => {
    const { user } = useAuth();
    if (!user) return null;
    const { addProperty } = usePropertyContext();

    const [step, setStep] = useState(2); // 2: Walkthrough only
    const [loading, setLoading] = useState(false);

    // Property State
    const [propertyName, setPropertyName] = useState("");
    const [propertySuburb, setPropertySuburb] = useState("");
    const [referralCode, setReferralCode] = useState("");

    // Walkthrough State
    const [walkthroughIndex, setWalkthroughIndex] = useState(0);

    const handleAcceptPopia = () => {
        setStep(1);
    };

    const handleNextStep = async () => {
        if (!propertyName.trim() || !propertySuburb) {
            toast({
                title: "Details Required",
                description: "Please provide a home nickname and your area/suburb.",
                variant: "destructive",
            });
            return;
        }

        setLoading(true);
        try {
            // Validate Referral Code if present
            if (referralCode.trim()) {
                const { data: agent, error } = await supabase
                    .from('agents')
                    .select('id, name')
                    .eq('code', referralCode.trim())
                    .maybeSingle();

                if (error || !agent) {
                    toast({
                        title: "Invalid Partner Code",
                        description: "The code you entered doesn't match any active partner.",
                        variant: "destructive",
                    });
                    setLoading(false);
                    return; // Stop here if code is invalid (strict mode)
                }

                // Link agent to profile immediately
                if (user) {
                    await supabase
                        .from('profiles')
                        .update({ agent_id: agent.id })
                        .eq('id', user.id);

                    toast({
                        title: "Partner Linked!",
                        description: `You are now connected with ${agent.name}.`,
                    });
                }
            }

            const property = await addProperty({
                name: propertyName.trim(),
                suburb: propertySuburb.trim() || null,
            });

            if (property) {
                setStep(2);
            }
        } catch (error) {
            console.error("Property creation error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleWalkthroughNext = () => {
        if (walkthroughIndex < slides.length - 1) {
            setWalkthroughIndex(prev => prev + 1);
        } else {
            handleFinalComplete();
        }
    };

    const handleWalkthroughPrev = () => {
        if (walkthroughIndex > 0) {
            setWalkthroughIndex(prev => prev - 1);
        }
    };

    const handleFinalComplete = async () => {
        setLoading(true);
        try {
            localStorage.setItem(`homeezi_welcome_${user?.id}`, "true");
            toast({
                title: "Welcome to HomeEzi!",
                description: "Everything is set up and ready for you.",
            });
            onComplete();
            onOpenChange(false);
        } catch (error) {
            console.error("Onboarding completion error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md overflow-hidden bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] border-none shadow-2xl rounded-[2.5rem] p-0">
                {step < 2 && (
                <div className="absolute top-0 left-0 right-0 h-1.5 flex gap-1 px-6 pt-6">
                    <div className={cn("h-full flex-1 rounded-full transition-all duration-700", step >= 0 ? "bg-primary" : "bg-gray-100")} />
                    <div className={cn("h-full flex-1 rounded-full transition-all duration-700", step >= 1 ? "bg-primary" : "bg-gray-100")} />
                </div>
                )}

                <div className="p-8 pt-12">
                    <AnimatePresence mode="wait">
                        {step === 0 ? (
                            <motion.div
                                key="step0"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.4, ease: "easeInOut" }}
                            >
                                <DialogHeader className="text-left">
                                    <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                                        <Check className="w-7 h-7 text-primary" />
                                    </div>
                                    <DialogTitle className="text-2xl font-extrabold tracking-tight">Your Privacy Matters</DialogTitle>
                                    <DialogDescription className="text-base text-muted-foreground mt-2 leading-relaxed">
                                        Before we start, we want to ensure you're comfortable with how HomeEzi handles your data in compliance with POPIA.
                                    </DialogDescription>
                                </DialogHeader>

                                <div className="space-y-4 py-8">
                                    <div className="bg-white/40 backdrop-blur-sm p-4 rounded-3xl space-y-4 border border-white/20">
                                        <div className="flex gap-3">
                                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                                                <Check className="w-3 h-3 text-green-600" />
                                            </div>
                                            <p className="text-sm text-foreground/80">
                                                <span className="font-bold">Privacy First:</span> We ask for as little as possible. Use a "Home Nickname" (alias) instead of your full address.
                                            </p>
                                        </div>
                                        <div className="flex gap-3">
                                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                                                <Check className="w-3 h-3 text-green-600" />
                                            </div>
                                            <p className="text-sm text-foreground/80">
                                                <span className="font-bold">Controlled Sharing:</span> Your contact info is <span className="underline">only</span> shared with service providers you have explicitly selected or contacted.
                                            </p>
                                        </div>
                                        <div className="flex gap-3">
                                            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5">
                                                <Check className="w-3 h-3 text-green-600" />
                                            </div>
                                            <p className="text-sm text-foreground/80">
                                                <span className="font-bold">Anonymized Stats:</span> We may use brand data (e.g. "4/10 homes have Brand X aircons") for distribution stats, but this is <span className="italic">never</span> linked to your specific identity or address.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    className="w-full rounded-2xl gap-2 py-7 text-lg font-bold shadow-lg shadow-primary/20 transition-all hover:shadow-primary/30 active:scale-[0.98]"
                                    onClick={handleAcceptPopia}
                                >
                                    I Agree & Continue
                                    <ArrowRight className="w-5 h-5" />
                                </Button>
                            </motion.div>
                        ) : step === 1 ? (
                            <motion.div
                                key="step1"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.4, ease: "easeInOut" }}
                            >
                                <DialogHeader className="text-left">
                                    <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                                        <Home className="w-7 h-7 text-primary" />
                                    </div>
                                    <DialogTitle className="text-2xl font-bold tracking-tight">Your Home Base</DialogTitle>
                                    <DialogDescription className="text-base text-muted-foreground mt-1">
                                        Let's start by giving your primary residence a nickname and area.
                                    </DialogDescription>
                                </DialogHeader>

                                <div className="space-y-4 py-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="prop-name" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Home Nickname *</Label>
                                        <Input
                                            id="prop-name"
                                            value={propertyName}
                                            onChange={(e) => setPropertyName(e.target.value)}
                                            placeholder="e.g. Primary Residence"
                                            className="rounded-2xl h-12 bg-gray-50/50 border-none px-4 focus-visible:ring-primary/20"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="prop-suburb" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Suburb / Area *</Label>
                                        <SuburbSelect
                                            value={propertySuburb}
                                            onSelect={setPropertySuburb}
                                            placeholder="Search your area..."
                                            className="h-12 rounded-2xl bg-gray-50/50 border-none"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="referral-code" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Partner Code (Optional)</Label>
                                        <Input
                                            id="referral-code"
                                            value={referralCode}
                                            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                                            placeholder="e.g. AGENT24"
                                            className="rounded-2xl h-12 bg-gray-50/50 border-none px-4 focus-visible:ring-primary/20 tracking-wider font-semibold placeholder:font-normal"
                                        />
                                    </div>
                                </div>

                                <Button
                                    className="w-full rounded-2xl gap-2 py-7 text-lg font-bold shadow-lg shadow-primary/20 transition-all hover:shadow-primary/30 active:scale-[0.98]"
                                    onClick={handleNextStep}
                                    disabled={loading || !propertyName.trim() || !propertySuburb}
                                >
                                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                                        <>
                                            Continue
                                            <ArrowRight className="w-5 h-5" />
                                        </>
                                    )}
                                </Button>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="step2"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.4, ease: "easeInOut" }}
                                className="flex flex-col items-center text-center h-[400px] justify-between"
                            >
                                <div className="flex-1 w-full flex flex-col items-center justify-center">
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={walkthroughIndex}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.3 }}
                                            className="flex flex-col items-center w-full"
                                        >
                                            <div className={cn(
                                                "w-24 h-24 rounded-3xl flex items-center justify-center mb-6 shadow-lg",
                                                slides[walkthroughIndex].bg
                                            )}>
                                                {(() => {
                                                    const Icon = slides[walkthroughIndex].icon;
                                                    return <Icon className={cn("w-10 h-10", slides[walkthroughIndex].color)} />;
                                                })()}
                                            </div>

                                            <h2 className="text-2xl font-bold tracking-tight mb-3 text-foreground">
                                                {slides[walkthroughIndex].title}
                                            </h2>

                                            <p className="text-muted-foreground leading-relaxed px-2 text-sm">
                                                {slides[walkthroughIndex].description}
                                            </p>
                                        </motion.div>
                                    </AnimatePresence>
                                </div>

                                <div className="w-full mt-8">
                                    <div className="flex justify-center gap-2 mb-6">
                                        {slides.map((_, idx) => (
                                            <div
                                                key={idx}
                                                className={cn(
                                                    "h-1.5 rounded-full transition-all duration-300",
                                                    idx === walkthroughIndex ? "w-6 bg-primary" : "w-1.5 bg-gray-200"
                                                )}
                                            />
                                        ))}
                                    </div>

                                    <div className="flex items-center gap-3 w-full">
                                        {walkthroughIndex > 0 && (
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={handleWalkthroughPrev}
                                                className="h-12 w-12 rounded-2xl shrink-0"
                                            >
                                                <ChevronLeft className="w-5 h-5" />
                                            </Button>
                                        )}

                                        <Button
                                            className="flex-1 h-12 rounded-2xl text-base font-semibold shadow-lg shadow-primary/20 hover:shadow-primary/30 active:scale-95 transition-all"
                                            onClick={handleWalkthroughNext}
                                            disabled={loading}
                                        >
                                            {walkthroughIndex === slides.length - 1 ? (
                                                <>
                                                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Get Started <Check className="w-5 h-5 ml-2" /></>}
                                                </>
                                            ) : (
                                                <>
                                                    Next <ArrowRight className="w-5 h-5 ml-2" />
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default OnboardingModal;
